import { useState } from "react"
import { Button } from "~/react/ui/button"
import { Separator } from "~/react/ui/separator"
import { Popover, PopoverTrigger, PopoverContent } from "~/react/ui/popover"
import { Input } from "~/react/ui/input"
import { Slider } from "~/react/ui/slider"
import { useCampaignContent } from "../campaign_content_context"
import { t } from "../i18n"
import { cn } from "~/lib/utils"

const DISTRIBUTION_MIN = 20
const DISTRIBUTION_MAX = 80
const DISTRIBUTION_STEP = 10

function clampToInt(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value.replace(/[^\d]/g, ""), 10)
  if (Number.isNaN(parsed)) return min
  return Math.max(min, Math.min(max, parsed))
}

function snapToStep(value: number): number {
  const clamped = Math.max(DISTRIBUTION_MIN, Math.min(DISTRIBUTION_MAX, value))
  return Math.round(clamped / DISTRIBUTION_STEP) * DISTRIBUTION_STEP
}

interface ConfigureDistributionControlsProps {
  percentA: number
  onPercentAChange: (value: number) => void
}

function ConfigureDistributionControls({ percentA, onPercentAChange }: ConfigureDistributionControlsProps) {
  const percentB = 100 - percentA

  return (
    <div className="variant-distribution-popover__content">
      <div className="variant-distribution-popover__title">
        {t("js.campaigns.content.ab_distribution_configure_title")}
      </div>

      <div className="variant-distribution-popover__labels-row">
        <span className="variant-distribution-popover__variant-label">{t("js.campaigns.content.ab_variant_a")}</span>
        <span className="variant-distribution-popover__variant-label">{t("js.campaigns.content.ab_variant_b")}</span>
      </div>

      <Slider
        size="sm"
        className="variant-distribution-popover__slider"
        value={[percentA]}
        min={DISTRIBUTION_MIN}
        max={DISTRIBUTION_MAX}
        step={DISTRIBUTION_STEP}
        showMarks
        onValueChange={(values) => {
          const next = snapToStep(values[0] ?? 50)
          onPercentAChange(next)
        }}
      />

      <div className="variant-distribution-popover__inputs-row">
        <Input
          size="sm"
          inputMode="numeric"
          className="variant-distribution-popover__percent-input"
          value={`${percentA}%`}
          onChange={(e) => {
            const next = snapToStep(clampToInt(e.target.value, DISTRIBUTION_MIN, DISTRIBUTION_MAX))
            onPercentAChange(next)
          }}
        />

        <Input
          size="sm"
          inputMode="numeric"
          className="variant-distribution-popover__percent-input"
          value={`${percentB}%`}
          onChange={(e) => {
            const nextB = snapToStep(clampToInt(e.target.value, DISTRIBUTION_MIN, DISTRIBUTION_MAX))
            const nextA = 100 - nextB
            onPercentAChange(nextA)
          }}
        />
      </div>
    </div>
  )
}

interface ConfigureDistributionPopoverProps {
  children: React.ReactNode
  currentPercentA: number
  onSave: (percentA: number) => void
}

function ConfigureDistributionPopover({ children, currentPercentA, onSave }: ConfigureDistributionPopoverProps) {
  const [open, setOpen] = useState(false)
  const [percentA, setPercentA] = useState(currentPercentA)

  // Reset local state when popover opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setPercentA(currentPercentA)
    }
    setOpen(isOpen)
  }

  const handleSave = () => {
    onSave(percentA)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="variant-distribution-popover"
        style={{ right: 0, left: "auto" }}
        side="bottom"
        align="end"
        sideOffset={4}
        collisionPadding={8}
      >
        <ConfigureDistributionControls percentA={percentA} onPercentAChange={setPercentA} />

        <Button
          type="button"
          variant="secondary"
          size="sm"
          fullWidth
          className="variant-distribution-popover__save"
          onClick={handleSave}
        >
          {t("js.campaigns.content.ab_distribution_save")}
        </Button>
      </PopoverContent>
    </Popover>
  )
}

export function Distribution() {
  const { abTestSettings, updateAbTestSettings } = useCampaignContent()

  const percentA = abTestSettings?.variant_a_percentage ?? 50
  const percentB = 100 - percentA

  const handleSave = async (newPercentA: number) => {
    await updateAbTestSettings(newPercentA)
  }

  return (
    <div className={cn("follow-up-editor__distribution-content")}>
      <dl className="compose-message-metas">
        <div className="compose-message-meta">
          <dt className="compose-message-meta__label">{t("js.campaigns.content.ab_distribution_label")}</dt>
          <dd className="compose-message-meta__value">
            {percentA}% – {percentB}%
          </dd>
        </div>
      </dl>
      <Separator orientation="vertical" className="follow-up-editor__distribution-divider" />
      <ConfigureDistributionPopover currentPercentA={percentA} onSave={handleSave}>
        <Button type="button" variant="linkGrey" size="sm">
          {t("js.campaigns.content.ab_distribution_configure")}
        </Button>
      </ConfigureDistributionPopover>
    </div>
  )
}
